import { useCallback, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import { NetworkStatus } from '@apollo/client';
import { Apollo } from 'src/gql/generated';

import type { AnalysisColumn, AnalysisRow } from './analysis-result-rows';

/** Rows one request loads. The server caps a page at 100. */
export const ANALYSIS_PAGE_SIZE = 50;

const NO_COLUMNS: AnalysisColumn[] = [];
const NO_ROWS: AnalysisRow[] = [];

/**
 * The rows of a recorded analysis query, read page by page under the viewer's
 * access. The first request also brings the columns, the current count and
 * whether the result changed since the answer was written. Later pages ask
 * only for rows, so scrolling never runs the count again.
 */
export function useAnalysisQueryRows({
  messageId,
  toolCallId,
}: {
  messageId: UUID;
  toolCallId: string;
}) {
  const { data, error, fetchMore, loading, networkStatus } = Apollo.useAnalysisQueryRowsQuery({
    variables: { limit: ANALYSIS_PAGE_SIZE, messageId, offset: 0, toolCallId },
  });
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  /**
   * A page shorter than asked for means the rows ran out before the count
   * said they would, which happens when documents change between requests.
   */
  const [exhausted, setExhausted] = useState(false);
  // `endReached` can fire again before the network status from the last call renders.
  const inFlightRef = useRef(false);

  const result = data?.analysisQueryRows;
  const rows: AnalysisRow[] = result?.rows ?? NO_ROWS;
  const totalCount = result?.totalCount ?? 0;
  const loadingMore = networkStatus === NetworkStatus.fetchMore;
  const hasMore = Boolean(result) && rows.length < totalCount && !exhausted && !loadMoreFailed;

  const loadMore = useCallback(async () => {
    if (!hasMore || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const page = await fetchMore({
        query: Apollo.AnalysisQueryRowsPageDocument,
        updateQuery: (previous, { fetchMoreResult }) => ({
          ...previous,
          analysisQueryRows: {
            ...previous.analysisQueryRows,
            rows: [...previous.analysisQueryRows.rows, ...fetchMoreResult.analysisQueryRows.rows],
          },
        }),
        variables: { limit: ANALYSIS_PAGE_SIZE, messageId, offset: rows.length, toolCallId },
      });
      if ((page.data?.analysisQueryRows?.rows?.length ?? 0) < ANALYSIS_PAGE_SIZE)
        setExhausted(true);
    } catch {
      setLoadMoreFailed(true);
    } finally {
      inFlightRef.current = false;
    }
  }, [fetchMore, hasMore, messageId, rows.length, toolCallId]);

  return {
    answeredTotalCount: result?.answeredTotalCount ?? 0,
    changedSinceAnswer: result?.changedSinceAnswer ?? false,
    columns: result?.columns ?? NO_COLUMNS,
    error: error ?? null,
    hasMore,
    loadMore,
    loadMoreFailed,
    // Apollo also reports `loading` during `fetchMore`, which would swap the table for a spinner.
    loading: loading && !loadingMore,
    loadingMore,
    rows,
    totalCount,
  };
}
